import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, OverlayTrigger, Tooltip, Form, Row, Spinner } from 'react-bootstrap';
import { Print, ReportPrint } from '@icons';
import { getPercentageESG, getPercentageSROI, putChangeStatusOnSubmitted, logout } from '@lib/api';
import { SuccessToast, DangerToast, InfoToast } from '@components/GlobalToastList';
import LoadingSpinner from '@components/loading-spinner';
import TutorialsButtonWrapper from 'components/TutorialsButtonWrapper';
import ProjectDonutChart from 'components/charts/projectDonutChart';

const ValutazioneHeader = ({
  activeStep,
  projectId,
  projectStatus,
  sectorCode,
  interventionClassCode,
  proponentCode,
  vanBenefits,
  translations,
  isUpdated,
  setIsUpdated,
  clientId,
  requestId,
  updatedAt,
  isLoading,
  setIsLoading,
}) => {
  const [progress, setProgress] = useState({});
  const [dataGeneral, setDataGeneral] = useState({});
  const [dataESG, setDataESG] = useState({});
  const [dataSROI, setDataSROI] = useState({});
  const [donutProgress, setDonutProgress] = useState([]);
  const [showModalAlert, setShowModalAlert] = useState(false);
  const [showModalCheckVanBenefits, setShowModalCheckVanBenefits] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmittingLoading, setIsSubmittingLoading] = useState(false);

  const fullDate = new Date(updatedAt);
  const date = `${fullDate.getDate()}-${fullDate.getMonth() + 1}-${fullDate.getFullYear()}`;
  const hours = `${fullDate.getHours()}:${fullDate.getMinutes()}`;

  const handleCloseModal = (setShowModal) => {
    setShowModal(false);
    setIsChecked(false);
  };

  const fetchPercentages = useCallback(async () => {
    if (!projectId) {
      setProgress({
        general: {
          total: 100,
          done: 0,
          notdone: 100,
          threshold: 80,
        },
        esg: {
          total: 100,
          done: 0,
          notdone: 100,
          threshold: 40,
        },
        sroi: {
          total: 100,
          done: 0,
          notdone: 100,
          threshold: 100,
        },
      });
      return;
    }

    try {
      const [percentageSROI, percentageESG] = await Promise.all([
        getPercentageSROI(projectId),
        getPercentageESG(projectId),
      ]);

      setProgress({
        general: {
          total: 100,
          done: 100,
          notdone: 0,
          threshold: 80,
        },
        esg: {
          total: 100,
          done: percentageESG,
          notdone: 100 - percentageESG,
          threshold: 40,
        },
        sroi: {
          total: 100,
          done: percentageSROI.percentage,
          notdone: 100 - percentageSROI.percentage,
          threshold: 100,
        },
      });
    } catch (err) {
      console.error('Errore nel caricamento delle percentuali', err);
      // opzionale: potresti voler mostrare un toast di errore qui
    }
  }, [projectId]);

  useEffect(() => {
    fetchPercentages();
  }, [fetchPercentages]);

  useEffect(() => {
    if (!isUpdated) return;

    const refresh = async () => {
      await fetchPercentages();
      setIsUpdated(false);
    };

    refresh();
  }, [isUpdated, fetchPercentages, setIsUpdated]);

  useEffect(() => {
    if (Object.keys(progress).length > 0) {
      setDataGeneral(progress.general);
      setDataESG(progress.esg);

      // Se ET o NOINTPURCH e general completato, forza sroi a 100
      if (
        (proponentCode === 'ET' || interventionClassCode === 'NOINTPURCH') &&
        progress.general.done === 100
      ) {
        const newSroi = {
          ...progress.sroi,
          done: 100,
          notdone: 0,
        };
        setDataSROI(newSroi);
      } else {
        setDataSROI(progress.sroi);
      }

      const tempDonutProgress = [
        progress.general.done,
        progress.esg.done,
        (proponentCode === 'ET' || interventionClassCode === 'NOINTPURCH') &&
        progress.general.done === 100
          ? 100
          : progress.sroi.done,
      ];
      setDonutProgress(tempDonutProgress);
    }
  }, [progress, proponentCode, sectorCode, interventionClassCode]);

  const checkVanBenefits = () => {
    if (interventionClassCode !== 'NOINTPURCH' && (vanBenefits <= 0 || !vanBenefits)) {
      setShowModalCheckVanBenefits(true);
    } else {
      setShowModalAlert(true);
    }
  };

  const changeToSubmitted = async (e) => {
    e.preventDefault();
    setIsSubmittingLoading(true);
    InfoToast('Salvataggio...', 'Stiamo apportando le modifiche', false);
    try {
      await putChangeStatusOnSubmitted(projectId, 30000);
      setIsSubmittingLoading(false);
      if (localStorage.getItem('ics_role') === '2' || localStorage.getItem('ics_role') === '3') {
        SuccessToast('Operazione conclusa', 'Progetto inviato correttamente');
      }

      setTimeout(() => {
        if (localStorage.getItem('ics_role') === '1') {
          logout();
          window.location = `/thanks?ndg=${clientId}&rapporto=${requestId}&date=${date}&hours=${hours}&id=${projectId}`;
        } else if (
          localStorage.getItem('ics_role') === '2' ||
          localStorage.getItem('ics_role') === '3'
        ) {
          window.location.assign('/progetti');
        }
      }, 1000);
    } catch (error) {
      setIsSubmittingLoading(false);
      DangerToast('Attenzione', 'Errore nella sottomissione del progetto');
    }
  };

  const checkLabel = (
    <>
      <p className='check-text mb-4'>
        “{translations.filter((trans) => trans.code === 'ICS108').map((trans) => trans.description)}
        &nbsp;
        <a
          className='a-check-text'
          href={translations
            .filter((trans) => trans.code === 'ICS144')
            .map((trans) => trans.description)}
          target='_blank'
          rel='noreferrer'>
          {translations.filter((trans) => trans.code === 'ICS144').map((trans) => trans.text)}
        </a>
        <span>
          {translations
            .filter((trans) => trans.code === 'ICS110')
            .map((trans) => trans.description)}
        </span>
      </p>

      <p className='check-text'>
        {translations.filter((trans) => trans.code === 'ICS111').map((trans) => trans.description)}
        &nbsp;
      </p>
    </>
  );

  return (
    <div className='simple-box impact mt-0'>
      <div className='d-flex flex-wrap justify-content-center'>
        <div className='chartBox impactChart mx-4 my-3'>
          <a href={projectId ? `/valutazione/${projectId}` : '#'}>
            <h5>
              1.{' '}
              {translations.filter((trans) => trans.code === 'ICS001').map((trans) => trans.text)}
            </h5>
          </a>
          {Object.keys(dataGeneral).length > 0 && Object.keys(progress).length > 0 ? (
            <ProjectDonutChart
              chartId='general'
              data={dataGeneral}
              current={activeStep === 'general'}
              progress={progress}
              style={{ marginTop: '1rem' }}
            />
          ) : null}

          {activeStep === 'general' && projectStatus !== 'SUBMITTED' && (
            <Button
              form='dati-generali-form'
              id='saveAll'
              type='submit'
              className='mt-3'
              disabled={isLoading}>
              {!isLoading ? (
                'SALVA'
              ) : (
                <span className='d-flex align-items-center' style={{ gap: '0.5rem' }}>
                  Caricamento
                  <Spinner animation='border' size='sm' />
                </span>
              )}
            </Button>
          )}
        </div>

        <div className='chartBox impactChart mx-4 my-3'>
          <a href={projectId ? `/valutazione/${projectId}/esg` : '#'}>
            <h5 style={{ whiteSpace: 'nowrap' }}>
              2.{' '}
              {translations.filter((trans) => trans.code === 'ICS029').map((trans) => trans.text)}
            </h5>
          </a>
          {Object.keys(dataGeneral).length > 0 ? (
            <ProjectDonutChart
              chartId='esg'
              data={dataESG}
              current={activeStep === 'esg'}
              progress={progress}
              style={{ marginTop: '1rem' }}
            />
          ) : null}

          {activeStep === 'esg' && projectStatus !== 'SUBMITTED' && (
            <Button
              form='valutazione-esg-form'
              id='saveAll'
              type='submit'
              className='mt-3'
              disabled={isLoading}>
              {!isLoading ? (
                'SALVA'
              ) : (
                <span className='d-flex align-items-center' style={{ gap: '0.5rem' }}>
                  Caricamento
                  <Spinner animation='border' size='sm' />
                </span>
              )}
            </Button>
          )}
        </div>

        <div
          className='chartBox impactChart mx-4 my-3'
          style={{
            pointerEvents:
              sectorCode === 'EVENTI' ||
              proponentCode === 'ET' ||
              proponentCode === 'LIGHTPRO' ||
              proponentCode === 'LIGHTNOPRO' ||
              interventionClassCode === 'NOINTPURCH'
                ? 'none'
                : '',
          }}>
          <a href={projectId ? `/valutazione/${projectId}/sroi` : '#'}>
            <h5>
              3.{' '}
              {translations.filter((trans) => trans.code === 'ICS017').map((trans) => trans.text)}
            </h5>
          </a>
          {Object.keys(dataGeneral).length > 0 ? (
            <ProjectDonutChart
              chartId='sroi'
              data={dataSROI}
              current={activeStep === 'sroi'}
              progress={progress}
              style={{ marginTop: '1rem' }}
            />
          ) : null}

          {activeStep === 'sroi' && proponentCode !== 'AUD' && projectStatus !== 'SUBMITTED' && (
            <Button
              form='valutazione-sroi-form'
              id='saveAll'
              type='submit'
              className='mt-3'
              disabled={isLoading}>
              {!isLoading ? (
                'SALVA'
              ) : (
                <span className='d-flex align-items-center' style={{ gap: '0.5rem' }}>
                  Caricamento
                  <Spinner animation='border' size='sm' />
                </span>
              )}
            </Button>
          )}
        </div>

        <div className='chartBox impactChart mx-4 my-3'>
          <h5>
            4.
            {translations.filter((trans) => trans.code === 'ICS030').map((trans) => trans.text)}
          </h5>

          {projectStatus !== 'SUBMITTED' ? (
            <div className='d-flex justify-content-center align-items-center flex-column'>
              <OverlayTrigger
                placement='top'
                delay={{ show: 200, hide: 400 }}
                overlay={
                  <Tooltip id='button-tooltip'>
                    Per inviare è necessario che tutte le sezioni siano completate al 100%
                  </Tooltip>
                }
                {...(donutProgress.every((value) => value === 100) ? { show: false } : null)}>
                <span className='d-inline-block' style={{ margin: '4rem auto 2rem auto' }}>
                  <Button
                    size='lg'
                    type='submit'
                    style={
                      !donutProgress.every((value) => value === 100)
                        ? { pointerEvents: 'none' }
                        : null
                    }
                    onClick={() => checkVanBenefits()}
                    disabled={
                      (donutProgress.length > 0 &&
                        !donutProgress.every((value) => value === 100)) ||
                      isLoading
                    }>
                    INVIA
                  </Button>
                </span>
              </OverlayTrigger>
              {donutProgress.length > 0 && donutProgress.every((value) => value === 100) && (
                <TutorialsButtonWrapper
                  isThanks
                  url={'https://www.youtube.com/embed/ktF2799YdK0?si=GbWTlGVcT9-Fidor'}
                  videoTitle={'Tutorial Sottomissione Progetto'}
                />
              )}
            </div>
          ) : (
            <>
              <Row className='flex-column align-items-center' style={{ marginTop: '4.5rem' }}>
                <Button size='lg' id='submitted-button' variant='outline-dark' disabled>
                  INVIATO
                </Button>

                <Row>
                  <Button
                    variant='link'
                    className='bg-transparent border-white mt-2'
                    href={`/thanks?ndg=${clientId}&rapporto=${requestId}&date=${date}&hours=${hours}&id=${projectId}`}
                    target='_blank'>
                    <Print
                      title={translations
                        .filter((trans) => trans.code === 'ICS097')
                        .map((trans) => trans.text)}
                      className='m-0 second-color'
                    />
                  </Button>
                  {proponentCode === 'ET' &&
                    projectStatus === 'SUBMITTED' &&
                    localStorage.getItem('ics_role') === '1' && (
                      <Button
                        variant='link'
                        className='bg-transparent border-white mt-2 ml-3'
                        href={`/projectresults/${btoa(projectId)}/${btoa(requestId)}`}
                        target='_blank'>
                        <ReportPrint
                          style={{ fill: '#08a2aa' }}
                          title={translations
                            .filter((trans) => trans.code === 'ICS134')
                            .map((trans) => trans.text)}
                          className='m-0 second-color'
                        />
                      </Button>
                    )}
                </Row>
              </Row>
            </>
          )}
        </div>
      </div>

      <Modal
        size='lg'
        show={showModalAlert}
        onHide={() => handleCloseModal(setShowModalAlert)}
        centered>
        {isSubmittingLoading && <LoadingSpinner />}
        <Modal.Header closeButton>
          <Modal.Title>Attenzione</Modal.Title>
        </Modal.Header>
        <Modal.Body className='px-5'>
          <p className='mb-4'>
            Effettuando la sottomissione non sarai più in grado di modificare i dati generali, le
            valutazioni e la compilazione del sondaggio associato.
          </p>
          <div>
            <Form.Group className='mb-3' controlId='checkPreSubmit'>
              <Form.Check
                type='checkbox'
                label={checkLabel}
                id='checkPreSubmit'
                onChange={() => setIsChecked(!isChecked)}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='success' onClick={() => handleCloseModal(setShowModalAlert)}>
            Annulla
          </Button>
          <Button variant='primary' disabled={!isChecked} onClick={(e) => changeToSubmitted(e)}>
            Conferma
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        size='lg'
        show={showModalCheckVanBenefits}
        onHide={() => handleCloseModal(setShowModalCheckVanBenefits)}
        centered>
        <Modal.Header closeButton>
          <Modal.Title>Attenzione</Modal.Title>
        </Modal.Header>
        <Modal.Body className='px-5'>
          <p className='my-3'>
            {translations.filter((trans) => trans.code === 'ICS117').map((trans) => trans.text)}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='success' onClick={() => handleCloseModal(setShowModalCheckVanBenefits)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ValutazioneHeader;
